/**
 * AdsManager Class
 * Manages interstitial and rewarded ads for a Pi Network application.
 * Features: smart pacing, ad preloading, session tracking.
 */

import arthereumProvider from "../../backend/ArthereumProvider";
import emitter, { EmitterEvents } from "./eventsEmitter";
import { isNotNullOrUndefined } from "./isNotNullOrUndefined";
import { RewardedAdStatusDTO, PiNetworkService } from "./PiBackendIntegration";
import { authWithPiNetwork } from "./PiIntegration";

class PiNetworkAdsManager {
  private adNetworkSupported = false;
  private sessionStartTime: number = Date.now();
  private adsShownThisSession: number = 0;
  private isPreloading: boolean = false;
  private preloadedAdReady: boolean = false;

  constructor() {
    this.init();
  }

  /**
   * Initialize the Pi SDK.
   */
  async init() {
    await (window as any).Pi.init({ version: "2.0", sandbox: false });
    const nativeFeaturesList = await (window as any).Pi.nativeFeaturesList();
    this.adNetworkSupported = nativeFeaturesList.includes("ad_network");

    // Start preloading the first ad
    this.preloadNextAd();
  }

  /**
   * Preload the next ad in the background to reduce loading delays.
   */
  async preloadNextAd() {
    if (this.isPreloading || !this.adNetworkSupported) return;
    
    this.isPreloading = true;
    try {
      const ready = await this.isAdReady("rewarded");
      if (!ready) {
        await this.requestAd("rewarded");
      }
      this.preloadedAdReady = true;
    } catch {
      this.preloadedAdReady = false;
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Get the optimal ad interval based on session depth.
   * Users who have been active longer tolerate more frequent ads.
   */
  getOptimalInterval(): number {
    const sessionMinutes = (Date.now() - this.sessionStartTime) / 60000;
    
    if (sessionMinutes < 0.75) return Infinity; // No auto-ads in first 45 seconds
    if (sessionMinutes < 3) return 90 * 1000; // 90 seconds
    if (sessionMinutes < 10) return 75 * 1000; // 75 seconds
    return 60 * 1000; // 60 seconds for engaged users
  }

  /**
   * Reset session tracking (call when user navigates away and comes back).
   */
  resetSession() {
    this.sessionStartTime = Date.now();
    this.adsShownThisSession = 0;
  }

  /**
   * Check if a specific type of ad is ready to be displayed.
   * @param {AdType} adType - The type of ad ("interstitial" or "rewarded").
   * @returns {Promise<boolean>} - Promise resolving to a boolean indicating ad readiness.
   */
  async isAdReady(adType: AdType): Promise<boolean> {
    const response = await (window as any).Pi.Ads.isAdReady(adType);
    return response.ready;
  }

  /**
   * Request a specific type of ad.
   * @param {AdType} adType - The type of ad ("interstitial" or "rewarded").
   * @returns {Promise<string>} - Promise resolving to the result of the ad request.
   */
  async requestAd(adType: AdType): Promise<string> {
    const response = await (window as any).Pi.Ads.requestAd(adType);
    return response.result;
  }

  /**
   * Display a specific type of ad.
   * @param {AdType} adType - The type of ad ("interstitial" or "rewarded").
   * @returns {Promise<ShowAdResponse>} - Promise resolving to the response of the ad display.
   */
  async showAd(adType: AdType): Promise<ShowAdResponse> {
    this.preloadedAdReady = false;
    const result = await (window as any).Pi.Ads.showAd(adType);
    this.adsShownThisSession++;
    
    // Start preloading the next ad immediately after showing one
    setTimeout(() => this.preloadNextAd(), 1000);
    
    return result;
  }

  /**
   * Handle interstitial ad flow.
   * @returns {Promise<void>}
   */
  async handleInterstitialAdFlow(): Promise<void> {
    try {
      if (!this.adNetworkSupported) {
        console.error(
          "Ad Network is not supported on this version of Pi Browser."
        );
        return;
      }
      const adReady = await this.isAdReady("interstitial");
      if (!adReady) {
        await this.requestAd("interstitial");
      }
      await this.showAd("interstitial");
    } catch (err) {
      console.error("Error handling interstitial ad flow:", err);
    }
  }

  async handleRewardedAdFlowFront(userId: string, onSuccess?: () => void): Promise<void> {
    const userData = await authWithPiNetwork();
    const username = userData?.username;
    try {
      if (!this.adNetworkSupported) {
        alert("Not supported in browser");
        console.error(
          "Ad Network is not supported on this version of Pi Browser."
        );
        return;
      }
      const adReady = await this.isAdReady("rewarded");
      if (!adReady) {
        const requestAdResponse = await this.requestAd("rewarded");
        if (requestAdResponse !== "AD_LOADED") {
          alert("Not loaded");
          return;
        }
      }
      const showAdResponse = await this.showAd("rewarded");
      if (showAdResponse.result === "AD_REWARDED") {
        if (isNotNullOrUndefined(showAdResponse.adId)) {
          const adStatus: RewardedAdStatusDTO =
            await PiNetworkService.connect().verifyRewardedAd(
              showAdResponse.adId!
            );
          if (
            adStatus.identifier === showAdResponse.adId
            // adStatus.mediator_ack_status === "granted"
          ) {
            await arthereumProvider.rewardAd(
              userId,
              username,
              adStatus.identifier
            );
            
            // Show ad reward modal
            emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
              modal: { name: "adsModal" },
            });
            onSuccess?.();
          }
        } else {
          // alert(
          //   "There is an issue with the ad provider sdk. Please retry again later"
          // );
        }
      } else {
        // Handle other ad display outcomes
      }
    } catch (err) {
      console.error("Error handling rewarded ad flow:", err);
      throw err;
    }
  }
}

const piNetworkAdsManager = new PiNetworkAdsManager();
export default piNetworkAdsManager;

// Types
type AdType = "interstitial" | "rewarded";

interface IsAdReadyResponse {
  type: AdType;
  ready: boolean;
}

interface RequestAdResponse {
  type: AdType;
  result: "AD_LOADED" | "AD_FAILED_TO_LOAD" | "AD_NOT_AVAILABLE";
}

interface ShowAdResponse {
  type: AdType;
  result:
    | "AD_CLOSED"
    | "AD_DISPLAY_ERROR"
    | "AD_NETWORK_ERROR"
    | "AD_NOT_AVAILABLE"
    | "AD_REWARDED"
    | "ADS_NOT_SUPPORTED"
    | "USER_UNAUTHENTICATED";
  adId?: string;
}
